/**
 * @file useWaitlist.js
 *
 * Waitlist Hooks
 *
 * useWaitlistJoin — patient-facing: join/leave a professional's waitlist (PublicProfile).
 * useWaitlistProfessional — professional-facing: toggle + count (MyProfile, Dashboard).
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { waitlistService } from "../services";
import { professionalService } from "../services";
import { WAITLIST_STRINGS } from "../constants/strings";

/**
 * Patient-facing hook for joining/leaving a professional's waitlist.
 * Used on PublicProfile when calendar is full.
 *
 * @param {number} professionalId
 * @param {boolean} waitlistEnabled - from public profile response
 * @param {number} waitlistCount - from public profile response
 */
export function useWaitlistJoin(professionalId, { waitlistEnabled = false, waitlistCount = 0 } = {}) {
  const [isJoined, setIsJoined] = useState(false);
  const [entryId, setEntryId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [durationDays, setDurationDays] = useState(30);

  const join = async () => {
    try {
      setIsLoading(true);
      const res = await waitlistService.join({ professionalId, durationDays });
      setIsJoined(true);
      setEntryId(res.data.entry?.id || res.data.id);
      toast.success(WAITLIST_STRINGS.JOINED_SUCCESS);
      return res.data;
    } finally {
      setIsLoading(false);
    }
  };

  const leave = async () => {
    if (!entryId) return;
    try {
      setIsLoading(true);
      await waitlistService.leave(entryId);
      setIsJoined(false);
      setEntryId(null);
      toast.success(WAITLIST_STRINGS.LEFT_SUCCESS);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isJoined,
    entryId,
    isLoading,
    durationDays,
    setDurationDays,
    join,
    leave,
    waitlistEnabled,
    waitlistCount,
  };
}

/**
 * Professional-facing hook for managing waitlist settings.
 * Used on MyProfile (toggle) and Dashboard (count).
 */
export function useWaitlistProfessional(enabled = true) {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(enabled);

  const fetchCount = useCallback(async () => {
    if (!enabled) return;
    try {
      setIsLoading(true);
      const res = await waitlistService.professionalCount();
      setCount(res.data?.count ?? res.data ?? 0);
    } catch {
      // Silently fail — count is non-critical
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  const toggleWaitlist = async (enabled) => {
    await professionalService.updateMe({ waitlistEnabled: enabled });
    toast.success(enabled ? WAITLIST_STRINGS.TOGGLE_ENABLED : WAITLIST_STRINGS.TOGGLE_DISABLED);
  };

  return { count, isLoading, refresh: fetchCount, toggleWaitlist };
}
